import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSession, PullIndicator, usePullToRefresh } from 'gunsi-auth'
import { useEven } from '../hooks/useEven.jsx'
import { useEvenI18n } from '../i18n/even.js'
import {
  youSummaryByCurrency, computeBalancesConverted, youOweFromDebts, itemCurrency,
} from '../util/balances.js'
import { getRatesForDates } from '../util/fx.js'
import { youOf, makeMemberLabel } from '../util/you.js'
import { fmtMoney } from '../format.js'
import { Select } from '../components/Select.jsx'
import { GroupFormSheet } from './GroupForm.jsx'

// Vista valute della lista (null = ogni gruppo nelle sue valute)
const VIEW_LS = 'gunsi_even_groupsview_v1'
function loadView() {
  try { return localStorage.getItem(VIEW_LS) || null } catch { return null }
}
function saveView(cur) {
  try {
    if (cur) localStorage.setItem(VIEW_LS, cur)
    else localStorage.removeItem(VIEW_LS)
  } catch { /* private mode: resta la vista separata, che è il default */ }
}

export function Groups() {
  const t = useEvenI18n()
  const { session } = useSession()
  const { groups, expenses, settlements, loading, currency: userCurrency, refresh } = useEven()
  const { listRef, pull, refreshing, ready } = usePullToRefresh(refresh)
  const [showNew, setShowNew] = useState(false)
  const [viewCur, setViewCur] = useState(loadView)
  const [ratesByDate, setRatesByDate] = useState(null)
  const [ratesLoading, setRatesLoading] = useState(false)
  const navigate = useNavigate()

  // Valute usate in tutti i gruppi: senza almeno due (o una diversa dalla
  // propria) convertire non aggiunge niente
  const allCurs = useMemo(() => {
    const byGroup = Object.fromEntries(groups.map(g => [g.id, g]))
    const set = new Set()
    for (const it of [...expenses, ...settlements]) set.add(itemCurrency(it, byGroup[it.groupId]))
    return [...set]
  }, [groups, expenses, settlements])
  const viewCurs = useMemo(
    () => [...new Set([userCurrency, ...allCurs])].filter(Boolean),
    [userCurrency, allCurs],
  )
  const canConvert = allCurs.length > 1 ||
    (allCurs.length === 1 && Boolean(userCurrency) && allCurs[0] !== userCurrency)

  function changeViewCur(cur) {
    setViewCur(cur)
    saveView(cur)
  }

  useEffect(() => {
    if (!viewCur || !canConvert) return
    let cancelled = false
    setRatesLoading(true)
    const dates = [...expenses, ...settlements]
      .map(i => (i.date || '').slice(0, 10)).filter(Boolean)
    getRatesForDates(dates).then(map => {
      if (cancelled) return
      setRatesByDate(map)
      setRatesLoading(false)
    })
    return () => { cancelled = true }
  }, [viewCur, canConvert, expenses, settlements])

  const unified = Boolean(viewCur) && canConvert
  const waitingRates = unified && (ratesLoading || !ratesByDate)

  if (loading) {
    return <div className="loading-screen"><div className="loading-spinner" /></div>
  }

  return (
    <main className="page page-groups">
      <header className="page-head">
        <h1>{t.groups.title}</h1>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          + {t.groups.new}
        </button>
      </header>

      {canConvert && (
        <div className="cur-view-row">
          <Select
            value={viewCur ?? ''}
            onChange={v => changeViewCur(v === '' ? null : v)}
            options={[
              { value: '', label: t.group.viewSeparate },
              ...viewCurs.map(c => ({ value: c, label: t.group.viewAllIn.replace('{cur}', c) })),
            ]}
            label={t.group.currencyView}
          />
        </div>
      )}

      <div className="ptr-wrap">
        <PullIndicator pull={pull} refreshing={refreshing} ready={ready} />
        <ul
          className="group-list"
          ref={listRef}
          style={{ transform: pull ? `translateY(${pull}px)` : undefined, transition: pull ? undefined : 'transform 180ms ease-out' }}
        >
          {groups.length === 0 && (
            <li className="empty-state">
              <p>{t.groups.empty}</p>
              <button className="btn btn-primary" onClick={() => setShowNew(true)}>
                + {t.groups.new}
              </button>
            </li>
          )}
          {[...groups]
            // Gli archiviati in fondo, sotto un separatore: restano
            // consultabili ma non intasano l'elenco di quelli attivi
            .sort((a, b) => Boolean(a.archivedAt) - Boolean(b.archivedAt))
            .map((g, i, arr) => {
            const firstArchived = Boolean(g.archivedAt) && !arr[i - 1]?.archivedAt
            const gExp = expenses.filter(e => e.groupId === g.id)
            const gSet = settlements.filter(s => s.groupId === g.id)
            const youId = youOf(g, session?.user?.id)?.id
            // Vista unificata: un solo saldo, convertito al tasso storico di
            // ogni movimento. Altrimenti una riga per valuta, come prima.
            const rows = unified && !waitingRates
              ? (() => {
                  const conv = computeBalancesConverted({
                    group: g, expenses: gExp, settlements: gSet,
                    target: viewCur, ratesByDate,
                  })
                  const net = youOweFromDebts(conv.debts, youId).net
                  return Math.abs(net) < 0.005 ? [] : [{ currency: viewCur, net }]
                })()
              : youSummaryByCurrency(g, gExp, gSet, youId)
                  .filter(s => Math.abs(s.net) >= 0.005)

            return (
              <Fragment key={g.id}>
              {firstArchived && <li className="people-sep">{t.groups.archived}</li>}
              <li
                className={`group-card${g.archivedAt ? ' is-archived' : ''}`}
                onClick={() => navigate(`/groups/${g.id}`)}
              >
                <div className="group-emoji">{g.emoji || '👥'}</div>
                <div className="group-meta">
                  <div className="group-name">{g.name}</div>
                  <div className="group-members">
                    {(() => {
                      const label = makeMemberLabel(g)
                      return (g.members ?? []).map(m => label(m.id)).join(', ')
                    })()}
                  </div>
                </div>
                <div className="group-status">
                  {g.archivedAt ? (
                    <div className="status-line status-even">{t.groups.archivedTag}</div>
                  ) : rows.length === 0 ? (
                    <div className="status-line status-even">{t.groups.settled}</div>
                  ) : (
                    rows.map(s => (
                      <div key={s.currency} className={`status-line status-${s.net > 0 ? 'owed' : 'owe'}`}>
                        {s.net > 0 ? t.groups.youAreOwed : t.groups.youOwe}{' '}
                        {unified && !waitingRates ? '≈ ' : ''}
                        {fmtMoney(Math.abs(s.net), s.currency)}
                      </div>
                    ))
                  )}
                </div>
              </li>
              </Fragment>
            )
          })}
        </ul>
      </div>

      <GroupFormSheet open={showNew} onClose={() => setShowNew(false)} />
    </main>
  )
}
